import { Injectable, signal, computed, EventEmitter } from '@angular/core';
import { openDB, IDBPDatabase } from 'idb';
import { Subscription, timer } from 'rxjs';

const DB_NAME = 'audio-settings';
const STORE_NAME = 'settings';
const MIC_KEY = 'selectedMicId';
const SPEAKER_KEY = 'selectedSpeakerId';

@Injectable({ providedIn: 'root' })
export class AudioDeviceService {
    private readonly _microphones = signal<MediaDeviceInfo[]>([]);
    private readonly _speakers = signal<MediaDeviceInfo[]>([]);

    readonly microphones = this._microphones.asReadonly();
    readonly speakers = this._speakers.asReadonly();

    private _savedMicId: string | null = null;
    private _savedSpeakerId: string | null = null;

    private db: Promise<IDBPDatabase> | null = null;

    testMicVolume = signal(0);
    micVolume = signal(4);
    micScale = signal(2);
    micTestRunning = signal(false);
    autoStopped = new EventEmitter();
    readonly testAudioReady = signal(false);

    private audioContext: AudioContext | null = null;
    private analyser: AnalyserNode | null = null;
    private stream: MediaStream | null = null;
    private dataArray: Uint8Array | null = null;
    private rafId: number | null = null;
    private mediaRecorder: MediaRecorder | null = null;
    private audioChunks: Blob[] = [];
    private testAudioUrl: string | null = null;
    private lastAudioUrl: string | null = null;
    private micStopSub: Subscription | null = null;

    async getDb(): Promise<IDBPDatabase> {
        if (!this.db) {
            if (typeof indexedDB === 'undefined') {
                throw new Error(
                    'IndexedDB is not available in this environment.',
                );
            }

            this.db = openDB(DB_NAME, 1, {
                upgrade(db) {
                    if (!db.objectStoreNames.contains(STORE_NAME)) {
                        db.createObjectStore(STORE_NAME);
                    }
                },
            });
        }

        return this.db;
    }

    getTestAudioUrl(): string | null {
        return this.testAudioUrl;
    }

    async toggleMicTest(): Promise<void> {
        if (this.lastAudioUrl) {
            URL.revokeObjectURL(this.lastAudioUrl);
            this.lastAudioUrl = null;
            this.testAudioUrl = null;
            this.testAudioReady.set(false);
        }

        if (this.micTestRunning()) {
            if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
                this.mediaRecorder.stop();
            }

            this.stopMicTest();

            if (this.stream) {
                this.stream.getTracks().forEach(t => t.stop());
                this.stream = null;
            }

            this.testAudioUrl = null;
            this.testAudioReady.set(false);
            this.micVolume.set(3);
            return;
        }

        this.testAudioUrl = null;
        this.testAudioReady.set(false);

        try {
            this.stream = await this.getUserMediaStream();

            this.audioContext = new AudioContext();
            const source = this.audioContext.createMediaStreamSource(
                this.stream,
            );
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 128;
            source.connect(this.analyser);
            this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);

            this.micTestRunning.set(true);
            this.animateMic();

            this.audioChunks = [];
            this.mediaRecorder = new MediaRecorder(this.stream);
            this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
                this.audioChunks.push(event.data);
            };
            this.mediaRecorder.onstop = () => {
                if (this.lastAudioUrl) {
                    URL.revokeObjectURL(this.lastAudioUrl);
                }

                const blob = new Blob(this.audioChunks, { type: 'audio/webm' });
                this.testAudioUrl = URL.createObjectURL(blob);
                this.lastAudioUrl = this.testAudioUrl;
                this.testAudioReady.set(true);
            };

            this.mediaRecorder.start();
            this.micStopSub = timer(10000).subscribe(() => {
                this.autoStopped.emit();
            });
        } catch (err) {
            console.error('Mic test failed', err);
        }
    }

    animateMic() {
        const loop = () => {
            if (!this.analyser || !this.dataArray) return;

            this.analyser.getByteTimeDomainData(this.dataArray);
            const volume = this.getVolume(this.dataArray);
            this.micVolume.set(volume);
            this.micScale.set(1 + volume / 100);

            this.rafId = requestAnimationFrame(loop);
        };

        loop();
    }

    getVolume(data: Uint8Array): number {
        const avg =
            data.reduce((sum, val) => sum + Math.abs(val - 128), 0) /
            data.length;
        return Math.min(100, avg);
    }

    stopMicTest() {
        this.micTestRunning.set(false);
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.stream?.getTracks().forEach(track => track.stop());
        this.audioContext?.close();

        this.rafId = null;
        this.stream = null;
        this.analyser = null;
        this.dataArray = null;
        this.audioContext = null;

        this.micVolume.set(0);
        this.micScale.set(1);

        if (this.micStopSub) {
            this.micStopSub.unsubscribe();
            this.micStopSub = null;
        }
    }

    readonly micId = computed(() => {
        const available = this._microphones();
        const saved = this._savedMicId;
        return saved && available.some(d => d.deviceId === saved)
            ? saved
            : (available[0]?.deviceId ?? null);
    });

    readonly speakerId = computed(() => {
        const available = this._speakers();
        const saved = this._savedSpeakerId;
        return saved && available.some(d => d.deviceId === saved)
            ? saved
            : (available[0]?.deviceId ?? null);
    });

    async loadAvailableDevices(): Promise<void> {
        try {
            await navigator.mediaDevices.getUserMedia({ audio: true });
            const devices = await navigator.mediaDevices.enumerateDevices();

            this._microphones.set(devices.filter(d => d.kind === 'audioinput'));
            this._speakers.set(devices.filter(d => d.kind === 'audiooutput'));

            const db = await this.getDb();
            this._savedMicId = await db.get(STORE_NAME, MIC_KEY);
            this._savedSpeakerId = await db.get(STORE_NAME, SPEAKER_KEY);
        } catch (err) {
            console.error('Unable to access devices or storage:', err);
        }
    }

    async saveMicId(id: string): Promise<void> {
        try {
            const db = await this.getDb();
            await db.put(STORE_NAME, id, MIC_KEY);
            this._savedMicId = id;
            this._microphones.set([...this._microphones()]);
        } catch (err) {
            console.warn('Failed to save mic ID:', err);
        }
    }

    async saveSpeakerId(id: string): Promise<void> {
        try {
            const db = await this.getDb();
            await db.put(STORE_NAME, id, SPEAKER_KEY);
            this._savedSpeakerId = id;
            this._speakers.set([...this._speakers()]);
        } catch (err) {
            console.warn('Failed to save speaker ID:', err);
        }
    }

    async getUserMediaStream(): Promise<MediaStream> {
        const id = this.micId();
        return navigator.mediaDevices.getUserMedia({
            audio: id ? { deviceId: { exact: id } } : true,
            video: false,
        });
    }

    async applySpeakerTo(audio: HTMLMediaElement): Promise<void> {
        const id = this.speakerId();
        if (id && typeof audio.setSinkId === 'function') {
            try {
                await audio.setSinkId(id);
            } catch (err) {
                console.warn('Could not set sink ID:', err);
            }
        }
    }
}
